/**
 * Administração › Sistemas plugados (ADR 0016).
 *
 * Cola o endereço de um sistema que já tem tela; o AURA lê o /manifest dele,
 * mostra o que vai criar e, ao confirmar, cria o item no menu (Serviços) com
 * os níveis declarados. Quem acessa e com que nível é definido em
 * Administração › Usuários e Perfis de acesso, como os módulos nativos.
 * O segredo do sistema aparece uma vez: é configurado no próprio sistema.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Plug, Search, Copy, RefreshCw, KeyRound, Snowflake, Play, AlertCircle, Globe } from 'lucide-react';
import toast from 'react-hot-toast';
import { plugAPI } from '../services/api';

const ROTULO_NIVEL = { leitor: 'Leitor', operador: 'Operador', gestor: 'Gestor' };

function Segredo({ valor, aviso, onFechar }) {
  const copiar = () => navigator.clipboard?.writeText(valor).then(() => toast.success('Segredo copiado'));
  return (
    <div className="card" style={{ padding: 16, marginBottom: 16, border: '1px solid #f0c36d', background: '#fffaf0' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
        <AlertCircle size={18} color="#b7791f" />
        <strong>{aviso}</strong>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <code style={{ flex: 1, padding: '8px 10px', background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 6, wordBreak: 'break-all' }}>{valor}</code>
        <button className="btn btn-secondary" onClick={copiar}><Copy size={14} /> Copiar</button>
        <button className="btn btn-primary" onClick={onFechar}>Já guardei</button>
      </div>
    </div>
  );
}

export default function AdminSistemasPlugados() {
  const [sistemas, setSistemas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [url, setUrl] = useState('');
  const [previa, setPrevia] = useState(null);
  const [abrirEm, setAbrirEm] = useState('quadro');
  const [urlPublica, setUrlPublica] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [segredo, setSegredo] = useState(null);   // { valor, aviso }

  const carregar = useCallback(async () => {
    try {
      const { data } = await plugAPI.listar();
      setSistemas(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao carregar os sistemas plugados');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const lerManifesto = async () => {
    if (!url.trim()) { toast.error('Informe o endereço do sistema'); return; }
    setOcupado(true); setPrevia(null);
    try {
      const { data } = await plugAPI.lerManifesto(url.trim());
      setPrevia(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Não foi possível ler o manifesto');
    } finally { setOcupado(false); }
  };

  const plugar = async () => {
    setOcupado(true);
    try {
      const { data } = await plugAPI.cadastrar(previa.url, abrirEm, urlPublica.trim());
      setSegredo({ valor: data.segredo, aviso: data.aviso });
      setPrevia(null); setUrl(''); setUrlPublica('');
      toast.success(`${data.sistema.nome} plugado. Defina quem acessa em Usuários ou Perfis de acesso.`);
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao plugar o sistema');
    } finally { setOcupado(false); }
  };

  const alterar = async (codigo, mudanca, mensagem) => {
    try {
      await plugAPI.alterar(codigo, mudanca);
      toast.success(mensagem);
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao alterar');
    }
  };

  const reler = async (s) => {
    try {
      await plugAPI.relerManifesto(s.codigo);
      toast.success(`Manifesto de ${s.nome} relido`);
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Não foi possível reler o manifesto');
    }
  };

  const novoSegredo = async (s) => {
    if (!window.confirm(`Gerar um novo segredo para ${s.nome}? O atual deixa de valer na hora e o sistema precisa ser configurado com o novo.`)) return;
    try {
      const { data } = await plugAPI.novoSegredo(s.codigo);
      setSegredo({ valor: data.segredo, aviso: data.aviso });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao gerar segredo');
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1><Plug size={22} style={{ verticalAlign: 'middle', marginRight: 8 }} />Sistemas plugados</h1>
        <p style={{ color: 'var(--gray-500)', marginTop: 4 }}>
          Sistemas com tela própria que entram no AURA pelo endereço. Quem acessa, com que nível, e travar ou
          congelar acesso: em Usuários e Perfis de acesso, como os módulos do AURA.
        </p>
      </div>

      {segredo && <Segredo valor={segredo.valor} aviso={segredo.aviso} onFechar={() => setSegredo(null)} />}

      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Plugar um sistema</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <input className="form-control" style={{ flex: 1 }} placeholder="https://sistema.operadora.com.br"
                 value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && lerManifesto()} />
          <button className="btn btn-secondary" onClick={lerManifesto} disabled={ocupado}><Search size={14} /> Ler manifesto</button>
        </div>

        {previa && (
          <div style={{ marginTop: 14, padding: 12, border: '1px solid var(--gray-200)', borderRadius: 8 }}>
            <div><strong>{previa.nome}</strong> <span style={{ color: 'var(--gray-500)' }}>({previa.codigo})</span></div>
            <div style={{ fontSize: 13, color: 'var(--gray-600)', margin: '4px 0 8px' }}>
              Entrada: <code>{previa.url}{previa.entrada}</code> · aparece em Serviços
            </div>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse', marginBottom: 10 }}>
              <tbody>
                {Object.entries(previa.niveis).map(([n, d]) => (
                  <tr key={n}>
                    <td style={{ padding: '3px 8px 3px 0', whiteSpace: 'nowrap', fontWeight: 600 }}>
                      {ROTULO_NIVEL[n]}{n === previa.nivel_base ? ' (ao liberar)' : ''}
                    </td>
                    <td style={{ padding: '3px 0', color: 'var(--gray-700)' }}>{d}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <label style={{ fontSize: 13, whiteSpace: 'nowrap' }}>Endereço no navegador</label>
              <input className="form-control" style={{ flex: 1 }} value={urlPublica} onChange={(e) => setUrlPublica(e.target.value)}
                     placeholder="opcional: /modulo-sistema quando o AURA serve o sistema pelo próprio domínio" />
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <label style={{ fontSize: 13 }}>Abrir</label>
              <select className="form-control" style={{ width: 'auto' }} value={abrirEm} onChange={(e) => setAbrirEm(e.target.value)}>
                <option value="quadro">dentro do AURA</option>
                <option value="aba">em aba nova</option>
              </select>
              <span style={{ flex: 1 }} />
              <button className="btn btn-primary" onClick={plugar} disabled={ocupado}><Plug size={14} /> Plugar</button>
            </div>
          </div>
        )}
      </div>

      {carregando ? (
        <div className="loading"><div className="spinner" /></div>
      ) : sistemas.length === 0 ? (
        <div className="empty-state">Nenhum sistema plugado ainda.</div>
      ) : (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table className="table" style={{ width: '100%' }}>
            <thead>
              <tr><th>Sistema</th><th>Endereço</th><th>Níveis</th><th>Abrir</th><th>Situação</th><th style={{ width: 1 }} /></tr>
            </thead>
            <tbody>
              {sistemas.map((s) => (
                <tr key={s.codigo}>
                  <td><strong>{s.nome}</strong><div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{s.codigo}</div></td>
                  <td style={{ fontSize: 13 }}>
                    <code>{s.url_publica || s.url_base}</code>
                    {s.url_publica && <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>interno: {s.url_base}</div>}
                  </td>
                  <td style={{ fontSize: 13 }}>{Object.keys(s.niveis).map((n) => ROTULO_NIVEL[n]).join(', ')}</td>
                  <td>
                    <select className="form-control" style={{ width: 'auto' }} value={s.abrir_em}
                            onChange={(e) => alterar(s.codigo, { abrir_em: e.target.value }, 'Forma de abrir alterada')}>
                      <option value="quadro">dentro do AURA</option>
                      <option value="aba">em aba nova</option>
                    </select>
                  </td>
                  <td>
                    {s.ativo
                      ? <span style={{ color: '#2f855a', fontWeight: 600 }}>Ativo</span>
                      : <span style={{ color: '#2b6cb0', fontWeight: 600 }}>Congelado</span>}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {s.ativo ? (
                      <button className="btn btn-secondary" title="Ninguém entra, nem administrador, até descongelar"
                              onClick={() => window.confirm(`Congelar ${s.nome}? Ninguém entra até descongelar.`) &&
                                alterar(s.codigo, { ativo: false }, `${s.nome} congelado`)}>
                        <Snowflake size={14} /> Congelar
                      </button>
                    ) : (
                      <button className="btn btn-secondary" onClick={() => alterar(s.codigo, { ativo: true }, `${s.nome} reativado`)}>
                        <Play size={14} /> Reativar
                      </button>
                    )}{' '}
                    <button className="btn btn-secondary" title="Endereço que o navegador usa para abrir o sistema"
                            onClick={() => {
                              const v = window.prompt('Endereço no navegador (ex.: /modulo-sistema). Vazio = usar o endereço interno.', s.url_publica || '');
                              if (v !== null) alterar(s.codigo, { url_publica: v.trim() }, 'Endereço no navegador alterado');
                            }}>
                      <Globe size={14} /> Navegador
                    </button>{' '}
                    <button className="btn btn-secondary" title="Atualiza nome, ícone e níveis a partir do sistema" onClick={() => reler(s)}>
                      <RefreshCw size={14} /> Reler
                    </button>{' '}
                    <button className="btn btn-secondary" title="Gera um novo segredo; o atual deixa de valer" onClick={() => novoSegredo(s)}>
                      <KeyRound size={14} /> Novo segredo
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
